import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X, Minus, Plus, ArrowRight } from "lucide-react";
import { useCart } from "../context/CartContext";
import { api, money } from "../lib/data";
import { toast } from "sonner";

// Feature flag: staged Shopify Storefront Cart migration. Standalone-item carts use the native
// checkout (/api/checkout/cart-create); any cart containing the Walk Kit uses the Draft Order
// flow (/api/checkout/create), since the kit's bundle discount only exists there today. Both
// flows redirect straight from the cart to Shopify's own checkout/invoice page, which collects
// contact and shipping details once. Set REACT_APP_NATIVE_CHECKOUT=false to force ALL carts back
// onto Draft Orders without touching this code — instant rollback.
const NATIVE_CHECKOUT_ENABLED = process.env.REACT_APP_NATIVE_CHECKOUT !== "false";

export default function CartDrawer() {
  const { items, open, setOpen, remove, setQty, subtotal, clear } = useCart();
  const hasWalkKit = items.some((i) => i.slug === "walk-kit");
  const useNativeCheckout = NATIVE_CHECKOUT_ENABLED && !hasWalkKit;
  const [creating, setCreating] = useState(false);

  const payloadItems = () =>
    items.map((i) => ({ slug: i.slug, name: i.name, quantity: i.qty, options: i.options }));

  // Standalone-item carts: no custom form — go straight from the cart to Shopify's own
  // checkout, which collects contact/shipping/billing/payment itself.
  const proceedNative = async () => {
    setCreating(true);
    try {
      const { data } = await api.post("/checkout/cart-create", { items: payloadItems() });
      if (data.checkout_url) {
        window.location.href = data.checkout_url;
        return;
      }
      toast.error(data.error || "We couldn't start checkout. Please try again shortly.");
    } catch (e) {
      toast.error("We couldn't start checkout just now. Please try again shortly.");
    } finally {
      setCreating(false);
    }
  };

  // Walk Kit / mixed carts: Draft Order flow, also going straight from the cart — Shopify's own
  // invoice checkout page collects contact/shipping/billing/payment, same as native checkout.
  const proceedDraftOrder = async () => {
    setCreating(true);
    try {
      const { data } = await api.post("/checkout/create", { items: payloadItems() });
      if (data.invoice_url) {
        window.location.href = data.invoice_url;
        return;
      }
      toast.error(data.error || "We couldn't start checkout. Please try again shortly.");
    } catch (e) {
      toast.error("We couldn't start checkout just now. Please try again shortly.");
    } finally {
      setCreating(false);
    }
  };

  const proceed = useNativeCheckout ? proceedNative : proceedDraftOrder;

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            key="overlay"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            onClick={() => setOpen(false)}
            className="fixed inset-0 z-[70] bg-charcoal/40 backdrop-blur-sm"
            data-testid="cart-overlay"
          />
          <motion.aside
            key="drawer"
            initial={{ x: "100%" }} animate={{ x: 0 }} exit={{ x: "100%" }}
            transition={{ type: "tween", duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
            className="fixed right-0 top-0 z-[80] flex h-full w-full max-w-[440px] flex-col bg-cream"
            data-testid="cart-drawer"
          >
            <div className="flex items-center justify-between border-b border-charcoal/10 px-6 py-5">
              <h2 className="font-fraunces text-2xl">Your bag</h2>
              <button data-testid="close-cart-button" onClick={() => setOpen(false)} className="rounded-full p-1 hover:bg-charcoal/5">
                <X strokeWidth={1.6} />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto px-6 py-4">
              {items.length === 0 ? (
                <div className="flex h-full flex-col items-center justify-center text-center" data-testid="cart-empty">
                  <p className="font-fraunces text-2xl">Nothing here yet.</p>
                  <p className="mt-2 font-manrope text-ink">Every mile starts with the first step.</p>
                </div>
              ) : (
                <ul className="flex flex-col gap-6">
                  {items.map((it) => (
                    <li key={it.id} className="flex gap-4" data-testid="cart-line-item">
                      <img src={it.image} alt={it.name} className="h-24 w-20 flex-shrink-0 bg-white object-contain p-1" />
                      <div className="flex flex-1 flex-col">
                        <div className="flex justify-between gap-2">
                          <p className="font-manrope font-semibold leading-tight">{it.name}</p>
                          <button onClick={() => remove(it.id)} data-testid="remove-line-item" className="text-ink hover:text-rust">
                            <X className="h-4 w-4" />
                          </button>
                        </div>
                        {it.optionsLabel && <p className="mt-1 font-manrope text-sm text-ink">{it.optionsLabel}</p>}
                        <div className="mt-auto flex items-center justify-between">
                          <div className="flex items-center gap-3 rounded-full border border-charcoal/20 px-3 py-1">
                            <button onClick={() => setQty(it.id, it.qty - 1)} data-testid="decrease-qty"><Minus className="h-4 w-4" /></button>
                            <span className="w-4 text-center font-manrope text-sm">{it.qty}</span>
                            <button onClick={() => setQty(it.id, it.qty + 1)} data-testid="increase-qty"><Plus className="h-4 w-4" /></button>
                          </div>
                          <span className="font-manrope font-semibold">{money(it.price * it.qty, it.currency)}</span>
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            {items.length > 0 && (
              <div className="border-t border-charcoal/10 px-6 py-5">
                <div className="flex items-center justify-between font-manrope">
                  <span className="text-ink">Subtotal</span>
                  <span className="text-lg font-semibold" data-testid="cart-subtotal">{money(subtotal)}</span>
                </div>
                <p className="mt-1 font-manrope text-xs text-ink">Free shipping in Canada · Taxes calculated at checkout.</p>
                <button
                  data-testid="checkout-button"
                  onClick={proceed}
                  disabled={creating}
                  className="group mt-4 flex w-full items-center justify-center gap-2 rounded-full bg-rust px-8 py-4 font-manrope font-semibold text-cream transition-colors hover:bg-charcoal disabled:opacity-60"
                >
                  {creating ? "Opening secure checkout…" : "Secure checkout"}{" "}
                  <ArrowRight className="hover-arrow h-4 w-4" />
                </button>
                <button onClick={clear} data-testid="clear-cart" className="mt-3 w-full text-center font-manrope text-sm text-ink underline underline-offset-4 hover:text-rust">
                  Clear bag
                </button>
              </div>
            )}
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
}
